// ./src/hooks/useTaxSettings.ts
//
// The account's reusable tax rates — the list behind BOTH the Taxes page (full CRUD) and the tax
// pickers on an event's selling settings and on a ticket type.
//
// ⚠⚠ ONE HOOK, NOT TWO. This started as a read-only loader for the event picker while the CRUD page
// did not exist. A second hook for the page would mean two ideas of what "the account's tax rates"
// are — and they would drift the first time one of them learned to sort, filter or paginate. The
// picker uses the read half and ignores the rest.
import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { removeBy, removeManyBy } from "@em/utils";

import { DEFAULT_PAGE_SIZE } from "@/config";
import { withSavedTax } from "@/components/generic/TaxSelect/taxRows";
import {
  TaxSettingService,
  TaxSetting,
  TaxSettingInput,
  TaxSettingFilter,
  SortItem,
  listPage,
} from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseTaxSettingsOptions {
  /** False keeps the hook idle — the event picker only needs rates on a ticketed event. */
  enabled?: boolean;
  autoLoad?: boolean;
  pageSize?: number;
  initialSearch?: string;
}

export interface UseTaxSettingsReturn {
  taxSettings: TaxSetting[];
  loading: boolean;
  loadingMore: boolean;
  isLoaded: boolean;
  isEmpty: boolean;
  hasMore: boolean;
  total: number;
  lastError: OpResult | null;
  isCreating: boolean;
  isUpdating: boolean;
  /** No id → "is anything being removed", for the bulk bar. */
  isRemoving: (id?: string) => boolean;
  search: string;
  setSearch: (value: string) => void;
  list: (filter?: TaxSettingFilter, page?: number, append?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
  create: (params: TaxSettingInput) => Promise<TaxSetting | null>;
  update: (id: string, params: TaxSettingInput) => Promise<TaxSetting | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;
}

const SORT: SortItem[] = [{ name: "name", order: "ASC" }];

export const useTaxSettings = (options: UseTaxSettingsOptions | boolean = {}): UseTaxSettingsReturn => {
  // ⚠ The event picker calls `useTaxSettings(isTicketed)`. Accepting the boolean keeps that call
  // site honest rather than silently loading nothing when someone passes the old shape.
  const opts: UseTaxSettingsOptions = typeof options === "boolean" ? { enabled: options } : options;
  const { enabled = true, autoLoad = true, pageSize = DEFAULT_PAGE_SIZE, initialSearch = "" } = opts;

  const [taxSettings, setTaxSettings] = useState<TaxSetting[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state before the
   * skeleton and then the data. Reported across the whole event space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [lastError, setLastError] = useState<OpResult | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(1);

  // ⚠ A race guard, not an optimisation. Typing in the search box fires overlapping list calls and
  // the slower one can land last — putting yesterday's results under today's query.
  const callIdRef = useRef(0);
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const searchRef = useRef(search);
  searchRef.current = search;

  const list = useCallback(
    async (filter?: TaxSettingFilter, targetPage = 1, append = false) => {
      if (!enabled) {
        setTaxSettings([]);
        setIsLoaded(true);

        return;
      }

      const myCall = ++callIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
      }
      setLastError(null);

      const effective: TaxSettingFilter = filter ?? (searchRef.current ? { searchKeyword: searchRef.current } : {});
      const result = await listPage(TaxSettingService.list, effective, { page: targetPage, pageSize: pageSizeRef.current }, SORT);

      if (myCall !== callIdRef.current) {
        return;
      }

      setLoading(false);
      setLoadingMore(false);
      setIsLoaded(true);

      if (result.code !== OP_RESULT_CODES.OK) {
        setLastError(result);

        return;
      }

      const rows = (result.getData() as TaxSetting[]) ?? [];
      const next = append ? [...taxSettings, ...rows] : rows;

      setTaxSettings(next);
      setPage(targetPage);

      // ⚠ `total` is what the server counted; the page length is only a hint. Falling back to
      // "a full page means there is more" is wrong on the exact boundary, which is why the
      // server number wins whenever it is present.
      const serverTotal = Number((result as any).total);
      const known = Number.isFinite(serverTotal) && serverTotal > 0 ? serverTotal : next.length;

      setTotal(known);
      setHasMore(Number.isFinite(serverTotal) && serverTotal > 0 ? next.length < serverTotal : rows.length === pageSizeRef.current);
    },
    [enabled, taxSettings],
  );

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    await list(undefined, page + 1, true);
  }, [list, loading, loadingMore, hasMore, page]);

  const reload = useCallback(async () => {
    await list(undefined, 1, false);
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(reload, isLoaded);

  useEffect(() => {
    if (autoLoad) {
      list(undefined, 1, false);
    }
    // ⚠ Deliberately keyed on `enabled` alone: `list` changes identity whenever the rows change,
    // and depending on it here would reload the list every time it loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, autoLoad]);

  const create = useCallback(async (params: TaxSettingInput): Promise<TaxSetting | null> => {
    setIsCreating(true);
    setLastError(null);

    const result = await TaxSettingService.create(params);

    setIsCreating(false);

    if (result.code !== OP_RESULT_CODES.OK) {
      setLastError(result);

      return null;
    }

    const created = ((result.getData() as TaxSetting[]) ?? [])[0] ?? null;

    if (created) {
      setTaxSettings((prev) => withSavedTax(prev, created));
      setTotal((n) => n + 1);
    }

    return created;
  }, []);

  const update = useCallback(async (id: string, params: TaxSettingInput): Promise<TaxSetting | null> => {
    setIsUpdating(true);
    setLastError(null);

    const result = await TaxSettingService.update({ id }, params);

    setIsUpdating(false);

    if (result.code !== OP_RESULT_CODES.OK) {
      setLastError(result);

      return null;
    }

    const updated = ((result.getData() as TaxSetting[]) ?? [])[0] ?? null;

    if (updated) {
      setTaxSettings((prev) => withSavedTax(prev, updated));
    }

    return updated;
  }, []);

  /** ⚠ Per-id, so two rows removed at once each spin their own button rather than freezing both. */
  const markRemoving = useCallback((ids: string[], on: boolean) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);

      for (const id of ids) {
        if (on) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }

      return next;
    });
  }, []);

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      markRemoving([id], true);
      setLastError(null);

      const result = await TaxSettingService.remove(id);

      markRemoving([id], false);

      if (result.code !== OP_RESULT_CODES.OK) {
        setLastError(result);

        return false;
      }

      setTaxSettings((prev) => removeBy(prev, id, "id"));
      setTotal((n) => Math.max(0, n - 1));

      return true;
    },
    [markRemoving],
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        return true;
      }

      markRemoving(ids, true);
      setLastError(null);

      const result = await TaxSettingService.removeMany(ids);

      markRemoving(ids, false);

      if (result.code !== OP_RESULT_CODES.OK) {
        setLastError(result);

        return false;
      }

      setTaxSettings((prev) => removeManyBy(prev, ids, "id"));
      setTotal((n) => Math.max(0, n - ids.length));

      return true;
    },
    [markRemoving],
  );

  const isRemoving = useCallback((id?: string) => (id ? removingIds.has(id) : removingIds.size > 0), [removingIds]);

  return {
    taxSettings,
    loading,
    loadingMore,
    isLoaded,
    isEmpty: isLoaded && taxSettings.length === 0,
    hasMore,
    total,
    lastError,
    isCreating,
    isUpdating,
    isRemoving,
    search,
    setSearch,
    list,
    loadMore,
    reload,
    create,
    update,
    remove,
    removeMany,
  };
};
